import type { ComponentProps } from "react";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  Boxes,
  CheckCircle2,
  CircleDollarSign,
  ExternalLink,
  GitBranch,
  Package,
  PackageCheck,
  Trash2,
  Undo2,
  X,
} from "lucide-react";

import { Button } from "@/components/ui/Button";
import { Input, Label, TextArea } from "@/components/ui/Input";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubSaveButton } from "@/components/ui/HubSaveButton";
import { HoverHintContent, ReadOnlyHoverLayer } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { buildManufacturingBatchDetailsPath } from "@/lib/manufacturingEditBatchPath";
import { ManufacturingLocationChip } from "@/lib/manufacturingLocationChip";
import {
  MANUFACTURING_HUB_READ_ONLY,
  formatCount,
  formatExternalLabourPerUnit,
  formatInternalLaborAppliedUnapplied,
  formatTrackedScanHours,
  formatUsdTwoDecimals,
  internalLaborCanSyncInternalOverhead,
  internalLaborOverAppliedVsTracked,
  internalLaborTrackedDollars,
  megaventoryUrlFor,
  effectiveWorkOrderOrderedQuantity,
  effectiveWorkOrderReceivedQuantity,
  formatDateTime,
  hubNativeWorkOrderCancelDisabledReason,
  isHubNativeWorkOrder,
  workOrderExecutionModeLabel,
  workOrderHasOpenMaterialDemand,
  workOrderPrimaryLabel,
  type RebuildRequestSummary,
  type WorkOrderDetail,
  type WorkOrderListRow,
} from "./workOrdersPageModel";
import { WorkOrderCurrentBomTable } from "./WorkOrderCurrentBomTable";
import { isOpenHubWorkOrderStatus, workOrderDeviationDisabledReason } from "./workOrderDeviationUtils";
import {
  CLAMP_QC_RECEIVE_HOVER_HINT,
  CLAMP_QC_RETURN_HOVER_HINT,
  CLAMP_QC_STARTED_HOVER_HINT,
  HUB_NATIVE_QUANTITY_LOCKED_HINT,
  clampQcJobStartedFromBom,
  isClampQcJobSku,
} from "./clampQcJobSku";
import { StatusPill } from "./workOrdersPageUi";

export function canRunMvMaterialActions(mvStatus: string | null | undefined): boolean {
  const lower = (mvStatus ?? "").trim().toLowerCase();
  return !/(closed|completed|cancelled|canceled|fullyreceived|fully received|fully invoiced|fullyinvoiced)/.test(lower);
}

function buildOrderLinkedToMegaventory(item: Pick<WorkOrderListRow, "mv_id" | "mv_number">): boolean {
  const id = item.mv_id;
  if (id != null && Number.isFinite(id) && id > 0) {
    return true;
  }
  return (item.mv_number ?? "").trim() !== "";
}

function applyInternalLaborDisabledReason(detail: WorkOrderDetail): string | null {
  if (isHubNativeWorkOrder(detail)) {
    return "Hub-native work orders apply internal labor on receipt, not via Megaventory sync.";
  }
  if (!buildOrderLinkedToMegaventory(detail)) {
    return "Work order has no Megaventory id or Megaventory work order number.";
  }
  if (!canRunMvMaterialActions(detail.mv_status)) {
    return "Internal labor sync is not available for this work order status.";
  }
  if (internalLaborTrackedDollars(detail) === null) {
    return "Set labor and overhead rates for every month that has floor scans on this work order in Manufacturing → Settings before applying internal labor.";
  }
  if (!internalLaborCanSyncInternalOverhead(detail)) {
    return "Megaventory internal overhead already matches tracked floor labor for this work order.";
  }
  return null;
}

type DisabledHintButtonProps = {
  hubWritesAllowed: boolean;
  /** Shown on hover when the button is disabled for a business rule (not hub read-only). */
  disabledReason: string | null;
} & ComponentProps<typeof Button>;

/** Action button: hover hint only when disabled (hub read-only or `disabledReason`). */
function DisabledHintButton({
  hubWritesAllowed,
  disabledReason,
  disabled,
  children,
  ...buttonProps
}: DisabledHintButtonProps): JSX.Element {
  const hoverHint = !hubWritesAllowed
    ? MANUFACTURING_HUB_READ_ONLY
    : disabledReason;
  const hintActive = hoverHint != null && hoverHint.length > 0;
  const buttonDisabled = !hubWritesAllowed || Boolean(disabled) || disabledReason != null;

  return (
    <ReadOnlyHoverLayer active={hintActive} hoverHint={hoverHint ?? ""} layout="inline">
      <Button {...buttonProps} disabled={buttonDisabled}>
        {children}
      </Button>
    </ReadOnlyHoverLayer>
  );
}

const EXTERNAL_LABOUR_HOVER_HINT =
  "Contract manufacturer external labor for this work order. The primary PO line auto-syncs until the first posted receipt. Additional charge POs (for example rework labor) are added separately and summed at receive.";

function WorkOrderExternalLabourSection({
  detail,
  hubWritesAllowed,
  patchExternalLabourState,
  addAdditionalChargeState,
}: {
  detail: WorkOrderDetail;
  hubWritesAllowed: boolean;
  patchExternalLabourState: {
    isPending: boolean;
    error: ApiError | null;
    mutate: (payload: { id: number; labourCost: number; chargeId?: number }) => void;
  };
  addAdditionalChargeState: {
    isPending: boolean;
    error: ApiError | null;
    mutate: (payload: { id: number; cmPoId: number; labourCost: number }) => void;
  };
}): JSX.Element | null {
  const charges = detail.external_labour_charges ?? [];
  if (charges.length === 0 && detail.cm_po_id == null) {
    return null;
  }

  const orderedQty = effectiveWorkOrderOrderedQuantity(detail);
  const total = detail.cm_external_labour_total ?? detail.cm_link_labour_cost ?? 0;
  const editable = detail.external_labour_charges_editable === true;
  const locked = detail.external_labour_locked === true;
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCmPoId, setNewCmPoId] = useState("");
  const [newLabourCost, setNewLabourCost] = useState("");
  const [additionalDrafts, setAdditionalDrafts] = useState<Record<number, string>>({});

  useEffect(() => {
    const next: Record<number, string> = {};
    for (const charge of charges) {
      if (charge.link_role === "additional") {
        next[charge.id] = String(charge.labour_cost);
      }
    }
    setAdditionalDrafts(next);
  }, [detail.id, charges]);

  const externalBusy = patchExternalLabourState.isPending || addAdditionalChargeState.isPending;

  return (
    <div className="col-span-2 space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <HoverHintContent hoverHint={EXTERNAL_LABOUR_HOVER_HINT} block>
          <div className="text-xs font-semibold uppercase tracking-wide text-slate-600">
            External labor (contract manufacturer)
          </div>
        </HoverHintContent>
        {locked ? (
          <span className="rounded bg-slate-200 px-2 py-0.5 text-xs font-medium text-slate-700">Locked after receipt</span>
        ) : (
          <span className="text-xs text-slate-600">Primary PO line auto-syncs until receive</span>
        )}
      </div>

      <div className="overflow-x-auto rounded border border-slate-200 bg-white">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-2">Role</th>
              <th className="px-3 py-2">CM PO</th>
              <th className="px-3 py-2 text-right">Labor</th>
            </tr>
          </thead>
          <tbody>
            {charges.map((charge) => (
              <tr key={charge.id} className="border-t border-slate-100">
                <td className="px-3 py-2 capitalize">{charge.link_role}</td>
                <td className="px-3 py-2">
                  <Link
                    to="/manufacturing-management/cm-po"
                    className="inline-flex items-center gap-1 font-mono text-xs text-brand-700 hover:text-brand-800"
                  >
                    {charge.cm_po_number || `PO #${charge.cm_po_id}`}
                    <ExternalLink className="h-3 w-3" aria-hidden />
                  </Link>
                </td>
                <td className="px-3 py-2 text-right">
                  {charge.link_role === "additional" && editable ? (
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={additionalDrafts[charge.id] ?? String(charge.labour_cost)}
                      onChange={(event) =>
                        setAdditionalDrafts((current) => ({
                          ...current,
                          [charge.id]: event.target.value,
                        }))
                      }
                      className="ml-auto max-w-[9rem] font-mono text-right"
                      disabled={externalBusy}
                    />
                  ) : (
                    <span className="font-mono">{formatUsdTwoDecimals(charge.labour_cost)}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot className="border-t border-slate-200 bg-slate-50">
            <tr>
              <td className="px-3 py-2 font-medium" colSpan={2}>Total at receive</td>
              <td className="px-3 py-2 text-right font-mono font-medium">{formatUsdTwoDecimals(total)}</td>
            </tr>
            <tr>
              <td className="px-3 py-2 text-xs text-slate-500" colSpan={3}>
                Per unit: {formatExternalLabourPerUnit(total, orderedQty)}
                {detail.po_line_unit_price != null ? (
                  <span className="ml-3">
                    Primary PO line: {formatUsdTwoDecimals(detail.po_line_unit_price)} / unit
                  </span>
                ) : null}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {editable ? (
        <div className="flex flex-wrap items-center gap-2">
          {charges
            .filter((charge) => charge.link_role === "additional")
            .map((charge) => {
              const draft = Number(additionalDrafts[charge.id]);
              const dirty =
                Number.isFinite(draft) &&
                Math.abs(draft - charge.labour_cost) >= 0.005;
              return (
                <ReadOnlyHoverLayer
                  key={charge.id}
                  active={!hubWritesAllowed}
                  hoverHint={MANUFACTURING_HUB_READ_ONLY}
                  layout="inline"
                >
                  <HubSaveButton
                    dirty={dirty}
                    dirtyHint={`Update additional labor for ${charge.cm_po_number}.`}
                    onClick={() =>
                      patchExternalLabourState.mutate({
                        id: detail.id,
                        labourCost: draft,
                        chargeId: charge.id,
                      })
                    }
                    isLoading={patchExternalLabourState.isPending}
                    disabled={!hubWritesAllowed || externalBusy || !dirty}
                  >
                    Save {charge.cm_po_number}
                  </HubSaveButton>
                </ReadOnlyHoverLayer>
              );
            })}
          <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="inline">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => setShowAddForm((open) => !open)}
              disabled={!hubWritesAllowed || externalBusy}
            >
              Add charge PO
            </Button>
          </ReadOnlyHoverLayer>
        </div>
      ) : null}

      {showAddForm && editable ? (
        <div className="grid grid-cols-2 gap-3 rounded border border-slate-200 bg-white p-3">
          <div>
            <div className="text-xs uppercase text-slate-500">CM PO id</div>
            <Input
              type="number"
              min="1"
              step="1"
              value={newCmPoId}
              onChange={(event) => setNewCmPoId(event.target.value)}
              className="mt-1 font-mono"
              placeholder="Hub CM PO id"
            />
          </div>
          <div>
            <div className="text-xs uppercase text-slate-500">Labor dollars</div>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={newLabourCost}
              onChange={(event) => setNewLabourCost(event.target.value)}
              className="mt-1 font-mono"
            />
          </div>
          <div className="col-span-2 flex justify-end">
            <Button
              type="button"
              size="sm"
              disabled={
                !hubWritesAllowed ||
                externalBusy ||
                !Number.isFinite(Number(newCmPoId)) ||
                Number(newCmPoId) <= 0 ||
                !Number.isFinite(Number(newLabourCost)) ||
                Number(newLabourCost) < 0
              }
              isLoading={addAdditionalChargeState.isPending}
              onClick={() => {
                addAdditionalChargeState.mutate({
                  id: detail.id,
                  cmPoId: Number(newCmPoId),
                  labourCost: Number(newLabourCost),
                });
                setShowAddForm(false);
                setNewCmPoId("");
                setNewLabourCost("");
              }}
            >
              Link additional charge
            </Button>
          </div>
        </div>
      ) : null}

      {patchExternalLabourState.error ? (
        <p className="text-xs text-rose-700">{patchExternalLabourState.error.message}</p>
      ) : null}
      {addAdditionalChargeState.error ? (
        <p className="text-xs text-rose-700">{addAdditionalChargeState.error.message}</p>
      ) : null}
    </div>
  );
}

export function WorkOrderDetailPanel({
  detail,
  isLoading,
  error,
  onClose,
  onOpenAllocateModal,
  allocateState,
  onOpenReceiveModal,
  hubNativeReceiveBlockedReason = null,
  receiveFromCmPo = false,
  onOpenReturnModal,
  receiveCloseState,
  returnMaterialsState,
  syncInternalLaborState,
  patchExternalLabourState,
  addAdditionalChargeState,
  onRebuildOpen,
  onDeviationOpen,
  onOpenCancelModal,
  onSaveHeader,
  saveHeaderState,
  cancelState,
  pendingDeviationCount,
  hubWritesAllowed,
  pendingRebuild,
  rebuildRequestsLoading,
  onApproveRebuild,
  onRejectRebuild,
  approveRebuildPending,
  rejectRebuildPending,
  approveRebuildError,
  rejectRebuildError,
}: {
  detail: WorkOrderDetail | null;
  isLoading: boolean;
  error: ApiError | null;
  onClose?: () => void;
  onOpenAllocateModal: () => void;
  allocateState: { isPending: boolean; error: ApiError | null; data: WorkOrderDetail | undefined };
  onOpenReceiveModal: () => void;
  hubNativeReceiveBlockedReason?: string | null;
  receiveFromCmPo?: boolean;
  onOpenReturnModal: () => void;
  receiveCloseState: { isPending: boolean; error: ApiError | null; data: WorkOrderDetail | undefined };
  returnMaterialsState: { isPending: boolean; error: ApiError | null; data: WorkOrderDetail | undefined };
  syncInternalLaborState: { isPending: boolean; error: ApiError | null; mutate: (buildOrderId: number) => void };
  patchExternalLabourState: {
    isPending: boolean;
    error: ApiError | null;
    mutate: (payload: { id: number; labourCost: number; chargeId?: number }) => void;
  };
  addAdditionalChargeState: {
    isPending: boolean;
    error: ApiError | null;
    mutate: (payload: { id: number; cmPoId: number; labourCost: number }) => void;
  };
  onRebuildOpen: () => void;
  onDeviationOpen: () => void;
  onOpenCancelModal: () => void;
  onSaveHeader: (payload: {
    id: number;
    orderedQuantity: number;
    manufacturerReferenceNumber: string;
    notes: string;
  }) => void;
  saveHeaderState: { isPending: boolean; error: ApiError | null };
  cancelState: { isPending: boolean; error: ApiError | null };
  pendingDeviationCount: number;
  hubWritesAllowed: boolean;
  pendingRebuild: RebuildRequestSummary | null;
  rebuildRequestsLoading: boolean;
  onApproveRebuild: (requestId: number) => void;
  onRejectRebuild: (requestId: number) => void;
  approveRebuildPending: boolean;
  rejectRebuildPending: boolean;
  approveRebuildError: ApiError | null;
  rejectRebuildError: ApiError | null;
}) {
  const hubNative = detail ? isHubNativeWorkOrder(detail) : false;
  const isCancelled = (detail?.hub_status ?? "").trim().toLowerCase() === "cancelled";
  const [headerQuantity, setHeaderQuantity] = useState("");
  const [headerReference, setHeaderReference] = useState("");
  const [headerNotes, setHeaderNotes] = useState("");
  const [headerFormError, setHeaderFormError] = useState<string | null>(null);
  const headerSyncKey =
    detail == null
      ? ""
      : [
          detail.id,
          effectiveWorkOrderOrderedQuantity(detail),
          detail.mfr_reference_number ?? "",
          detail.notes ?? "",
        ].join("|");
  useEffect(() => {
    if (detail == null || !isHubNativeWorkOrder(detail)) return;
    setHeaderQuantity(String(effectiveWorkOrderOrderedQuantity(detail)));
    setHeaderReference(detail.mfr_reference_number ?? "");
    setHeaderNotes(detail.notes ?? "");
    setHeaderFormError(null);
    // headerSyncKey already includes the work order id and the saved field values.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerSyncKey]);
  const clampQcJob = detail != null && isClampQcJobSku(detail.sku);
  const clampQcStarted = clampQcJob && clampQcJobStartedFromBom(detail?.bom);
  const quantityLocked =
    isCancelled ||
    (detail != null && !isOpenHubWorkOrderStatus(detail.hub_status)) ||
    (detail != null && effectiveWorkOrderReceivedQuantity(detail) > 0) ||
    clampQcJobStartedFromBom(detail?.bom);
  const cancelDisabledReason =
    clampQcStarted
      ? CLAMP_QC_STARTED_HOVER_HINT
      : detail && hubWritesAllowed && hubNative && !isCancelled
        ? hubNativeWorkOrderCancelDisabledReason(detail)
        : null;
  const allocateDisabledReason =
    hubWritesAllowed && !workOrderHasOpenMaterialDemand(detail?.bom ?? [])
      ? "Nothing remaining to allocate."
      : null;
  const rebuildDisabledReason =
    hubWritesAllowed && (detail?.mv_status ?? "").trim().toLowerCase() !== "pending"
      ? "Rebuild only works on Pending MV work orders."
      : null;
  const deviationDisabledReason = clampQcStarted
    ? CLAMP_QC_STARTED_HOVER_HINT
    : detail && hubWritesAllowed
      ? workOrderDeviationDisabledReason({ hubStatus: detail.hub_status })
      : null;
  const applyInternalLaborReason = detail ? applyInternalLaborDisabledReason(detail) : null;
  const receiveDisabledReason = clampQcJob
    ? CLAMP_QC_RECEIVE_HOVER_HINT
    : detail?.cm_po_number && !receiveFromCmPo
      ? "Finished goods for this work order are posted from CM PO receiving."
    : hubNativeReceiveBlockedReason != null && hubNativeReceiveBlockedReason.length > 0
      ? hubNativeReceiveBlockedReason
      : detail && hubWritesAllowed && !hubNative && !buildOrderLinkedToMegaventory(detail)
      ? "Work order has no Megaventory id or Megaventory work order number."
      : detail && hubWritesAllowed && !hubNative && internalLaborOverAppliedVsTracked(detail)
        ? "Megaventory internal overhead is above tracked floor labor. Remove internal labor on this panel before receiving finished goods."
        : null;
  const returnDisabledReason = clampQcJob
    ? CLAMP_QC_RETURN_HOVER_HINT
    : detail && hubWritesAllowed && !hubNative && !buildOrderLinkedToMegaventory(detail)
      ? "Work order has no Megaventory id or Megaventory work order number."
      : detail && hubWritesAllowed && !canRunMvMaterialActions(detail.mv_status)
        ? "Return materials is not available for this work order status."
        : null;

  const apiErrorDetail =
    error?.body != null &&
    typeof error.body === "object" &&
    "detail" in error.body &&
    typeof (error.body as { detail?: unknown }).detail === "string"
      ? String((error.body as { detail: string }).detail).trim()
      : "";

  if (isLoading && detail == null) {
    return (
      <Card>
        <CardBody className="py-12 text-center text-sm text-slate-500">
          <Boxes className="mx-auto h-8 w-8 text-slate-300" aria-hidden />
          <p className="mt-3">Loading work order…</p>
        </CardBody>
      </Card>
    );
  }

  if (error != null && detail == null) {
    return (
      <Card>
        <CardBody className="space-y-3 py-8">
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            <div>
              <p className="font-medium">{error.message}</p>
              {apiErrorDetail.length > 0 && apiErrorDetail !== error.message.trim() ? (
                <p className="mt-1 text-red-600">{apiErrorDetail}</p>
              ) : null}
            </div>
          </div>
          {onClose ? (
            <Button variant="secondary" size="sm" onClick={onClose} type="button">
              Close
            </Button>
          ) : null}
        </CardBody>
      </Card>
    );
  }

  if (detail == null) {
    return (
      <Card>
        <CardBody className="py-12 text-center text-sm text-slate-500">
          <Boxes className="mx-auto h-8 w-8 text-slate-300" aria-hidden />
          <p className="mt-3">Pick a work order to see its current BOM and run actions.</p>
        </CardBody>
      </Card>
    );
  }

  const megaventoryUrl = megaventoryUrlFor(detail);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex min-w-0 items-center gap-1.5">
              <CardTitle className="min-w-0">{workOrderPrimaryLabel(detail)}</CardTitle>
              {megaventoryUrl ? (
                <a
                  href={megaventoryUrl}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="shrink-0 text-brand-600 hover:text-brand-700"
                  aria-label={`Open ${workOrderPrimaryLabel(detail)} in Megaventory`}
                >
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                </a>
              ) : null}
            </div>
            {detail ? (
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-slate-500">
                <StatusPill row={detail} />
                <span className="rounded bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">
                  {workOrderExecutionModeLabel(detail)}
                </span>
                <span>internal {detail.number}</span>
                {detail.location_id != null && detail.location_id > 0 ? (
                  <ManufacturingLocationChip
                    locationId={detail.location_id}
                    fallbackLabel={detail.location_name ?? undefined}
                  />
                ) : detail.location_name ? (
                  <ManufacturingLocationChip locationId={0} fallbackLabel={detail.location_name} />
                ) : null}
                {(detail.batch_ids ?? []).length > 0 ? (
                  <span className="inline-flex flex-wrap items-center gap-1">
                    <span>linked</span>
                    {(detail.batch_ids ?? []).map((batchId, index) => (
                      <span key={batchId}>
                        {index > 0 ? <span>, </span> : null}
                        <Link
                          className="font-medium text-brand-700 hover:text-brand-900"
                          to={buildManufacturingBatchDetailsPath(batchId)}
                        >
                          Batch {batchId}
                        </Link>
                      </span>
                    ))}
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          {onClose ? (
            <button
              onClick={onClose}
              className="rounded-md border border-slate-200 bg-white p-1 text-slate-500 hover:text-slate-900"
              aria-label="Close work order detail"
              type="button"
            >
              <X className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
        </div>
      </CardHeader>
      <CardBody className="space-y-4">
        {error ? (
          <div className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            <AlertTriangle className="h-4 w-4 shrink-0" aria-hidden />
            <div>{error.message}</div>
          </div>
        ) : null}

        {rebuildRequestsLoading && detail ? (
          <div className="text-xs text-slate-500">Checking pending rebuild requests…</div>
        ) : null}

        {hubNative && pendingDeviationCount > 0 && detail ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
            <div className="font-semibold">
              {pendingDeviationCount} deviation{pendingDeviationCount === 1 ? "" : "s"} pending allocation
              resolution
            </div>
            <p className="mt-1 text-xs text-amber-900">
              Return allocated material for affected components before allocate or receive.
            </p>
          </div>
        ) : null}

        {pendingRebuild && detail && !hubNative ? (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
            <div className="font-semibold">Pending connector BOM rebuild #{pendingRebuild.id}</div>
            <p className="mt-1 text-xs text-amber-900">
              Submitted {pendingRebuild.requested_at}. Megaventory does not run until you approve or reject below.
              {pendingRebuild.reason ? ` Note: ${pendingRebuild.reason}` : ""} Expires {pendingRebuild.expires_at}.
            </p>
            <ReadOnlyHoverLayer active={!hubWritesAllowed} hoverHint={MANUFACTURING_HUB_READ_ONLY} layout="block">
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  isLoading={approveRebuildPending}
                  disabled={!hubWritesAllowed || approveRebuildPending || rejectRebuildPending}
                  onClick={() => onApproveRebuild(Number(pendingRebuild.id))}
                >
                  Approve and run Megaventory rebuild
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  isLoading={rejectRebuildPending}
                  disabled={!hubWritesAllowed || approveRebuildPending || rejectRebuildPending}
                  onClick={() => onRejectRebuild(Number(pendingRebuild.id))}
                >
                  Reject request
                </Button>
              </div>
            </ReadOnlyHoverLayer>
            {approveRebuildError ? (
              <p className="mt-2 text-xs text-red-700">{approveRebuildError.message}</p>
            ) : null}
            {rejectRebuildError ? (
              <p className="mt-2 text-xs text-red-700">{rejectRebuildError.message}</p>
            ) : null}
          </div>
        ) : null}

        {isLoading && detail == null ? (
          <div className="text-sm text-slate-500">Loading work order…</div>
        ) : null}

        {detail ? (
          <>
            {isCancelled ? (
              <div className="rounded-md border border-slate-300 bg-slate-100 p-3 text-sm text-slate-800">
                <div className="font-semibold text-slate-900">Cancelled</div>
                {detail.cancelled_at ? (
                  <p className="mt-1 text-xs text-slate-600">
                    {formatDateTime(detail.cancelled_at)}
                    {detail.cancelled_by_display_name || detail.cancelled_by_email
                      ? ` by ${detail.cancelled_by_display_name ?? detail.cancelled_by_email}`
                      : ""}
                  </p>
                ) : null}
                {detail.cancellation_reason ? (
                  <p className="mt-2 whitespace-pre-wrap text-sm">{detail.cancellation_reason}</p>
                ) : null}
              </div>
            ) : null}

            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs uppercase text-slate-500">Finished good</div>
                <div className="font-mono text-sm">{detail.sku ?? "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500">Quantity</div>
                <div className="font-mono text-sm">
                  {formatCount(effectiveWorkOrderReceivedQuantity(detail))} received /{" "}
                  {formatCount(effectiveWorkOrderOrderedQuantity(detail))} ordered
                </div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500">MV id</div>
                <div className="font-mono text-sm">{detail.mv_id ?? "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500">MV reference</div>
                <div className="font-mono text-sm">{detail.mv_reference_number ?? "—"}</div>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500">Internal labor (applied / unapplied)</div>
                <HoverHintContent hoverHint={formatInternalLaborAppliedUnapplied(detail).title} block>
                  <div className="font-mono text-sm">{formatInternalLaborAppliedUnapplied(detail).label}</div>
                </HoverHintContent>
              </div>
              <div>
                <div className="text-xs uppercase text-slate-500">Tracked scan time</div>
                <div className="font-mono text-sm">{formatTrackedScanHours(detail.labor_tracked_seconds)}</div>
              </div>
              <WorkOrderExternalLabourSection
                detail={detail}
                hubWritesAllowed={hubWritesAllowed}
                patchExternalLabourState={patchExternalLabourState}
                addAdditionalChargeState={addAdditionalChargeState}
              />
              {!hubNative && detail.notes ? (
                <div className="col-span-2">
                  <div className="text-xs uppercase text-slate-500">MV comments</div>
                  <div className="whitespace-pre-wrap text-sm">{detail.notes}</div>
                </div>
              ) : null}
            </div>

            {hubNative && !isCancelled ? (
              <form
                className="space-y-3 rounded-md border border-slate-200 bg-white p-3"
                onSubmit={(event) => {
                  event.preventDefault();
                  const quantity = Math.round(Number(headerQuantity));
                  if (!Number.isFinite(quantity) || quantity <= 0) {
                    setHeaderFormError("Ordered quantity must be a positive whole number.");
                    return;
                  }
                  setHeaderFormError(null);
                  onSaveHeader({
                    id: detail.id,
                    orderedQuantity: quantity,
                    manufacturerReferenceNumber: headerReference,
                    notes: headerNotes,
                  });
                }}
              >
                <div className="text-sm font-semibold text-slate-900">Work order details</div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <Label htmlFor="hub-work-order-quantity">Ordered quantity</Label>
                    {quantityLocked ? (
                      <HoverHintContent hoverHint={HUB_NATIVE_QUANTITY_LOCKED_HINT} block>
                        <Input
                          id="hub-work-order-quantity"
                          inputMode="numeric"
                          value={headerQuantity}
                          disabled
                          onChange={(event) => setHeaderQuantity(event.target.value)}
                        />
                      </HoverHintContent>
                    ) : (
                      <Input
                        id="hub-work-order-quantity"
                        inputMode="numeric"
                        value={headerQuantity}
                        disabled={!hubWritesAllowed || saveHeaderState.isPending}
                        onChange={(event) => setHeaderQuantity(event.target.value)}
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="hub-work-order-reference">Reference</Label>
                    <Input
                      id="hub-work-order-reference"
                      value={headerReference}
                      disabled={!hubWritesAllowed || saveHeaderState.isPending}
                      onChange={(event) => setHeaderReference(event.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="hub-work-order-notes">Notes</Label>
                  <TextArea
                    id="hub-work-order-notes"
                    value={headerNotes}
                    disabled={!hubWritesAllowed || saveHeaderState.isPending}
                    onChange={(event) => setHeaderNotes(event.target.value)}
                  />
                </div>
                <Button type="submit" size="sm" disabled={!hubWritesAllowed || saveHeaderState.isPending}>
                  {saveHeaderState.isPending ? "Saving…" : "Save details"}
                </Button>
                {headerFormError ? <p className="text-xs text-rose-700">{headerFormError}</p> : null}
                {saveHeaderState.error ? (
                  <p className="text-xs text-rose-700">{saveHeaderState.error.message}</p>
                ) : null}
              </form>
            ) : null}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-slate-900">Current BOM</h4>
                <span className="text-xs text-slate-500">
                  {detail.bom.length} line{detail.bom.length === 1 ? "" : "s"}
                </span>
              </div>
              <WorkOrderCurrentBomTable bom={detail.bom} />
            </div>

            <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
              <div className="text-sm font-semibold text-slate-900">Actions</div>
              <div className="flex flex-wrap gap-2">
                {hubNative && !isCancelled ? (
                  <DisabledHintButton
                    hubWritesAllowed={hubWritesAllowed}
                    disabledReason={cancelDisabledReason}
                    variant="danger"
                    size="sm"
                    isLoading={cancelState.isPending}
                    onClick={onOpenCancelModal}
                    disabled={cancelState.isPending}
                  >
                    <Trash2 className="h-4 w-4" aria-hidden /> Cancel work order
                  </DisabledHintButton>
                ) : null}
                {!isCancelled ? (
                  <>
                <DisabledHintButton
                  hubWritesAllowed={hubWritesAllowed}
                  disabledReason={allocateDisabledReason}
                  variant="secondary"
                  size="sm"
                  isLoading={allocateState.isPending}
                  onClick={onOpenAllocateModal}
                  disabled={!workOrderHasOpenMaterialDemand(detail.bom) || allocateState.isPending}
                >
                  <CheckCircle2 className="h-4 w-4" aria-hidden /> Allocate materials
                </DisabledHintButton>
                {hubNative ? (
                  <DisabledHintButton
                    hubWritesAllowed={hubWritesAllowed}
                    disabledReason={deviationDisabledReason}
                    variant="secondary"
                    size="sm"
                    onClick={onDeviationOpen}
                  >
                    <GitBranch className="h-4 w-4" aria-hidden /> BOM deviation
                  </DisabledHintButton>
                ) : (
                  <DisabledHintButton
                    hubWritesAllowed={hubWritesAllowed}
                    disabledReason={rebuildDisabledReason}
                    variant="secondary"
                    size="sm"
                    onClick={onRebuildOpen}
                    disabled={
                      (detail.mv_status ?? "").trim().toLowerCase() !== "pending" || rebuildRequestsLoading
                    }
                  >
                    <PackageCheck className="h-4 w-4" aria-hidden /> Rebuild BOM
                  </DisabledHintButton>
                )}
                {!hubNative ? (
                  <DisabledHintButton
                    hubWritesAllowed={hubWritesAllowed}
                    disabledReason={applyInternalLaborReason}
                    variant="secondary"
                    size="sm"
                    onClick={() => syncInternalLaborState.mutate(detail.id)}
                    disabled={syncInternalLaborState.isPending}
                    isLoading={syncInternalLaborState.isPending}
                  >
                    <CircleDollarSign className="h-4 w-4" aria-hidden />
                    {internalLaborOverAppliedVsTracked(detail)
                      ? "Remove internal labor"
                      : "Apply internal labor"}
                  </DisabledHintButton>
                ) : null}
                <DisabledHintButton
                  hubWritesAllowed={hubWritesAllowed}
                  disabledReason={receiveDisabledReason}
                  variant="secondary"
                  size="sm"
                  className="shrink-0 whitespace-nowrap"
                  onClick={onOpenReceiveModal}
                  disabled={receiveCloseState.isPending}
                  isLoading={receiveCloseState.isPending}
                >
                  <Package className="h-4 w-4" aria-hidden /> Receive finished good
                </DisabledHintButton>
                <DisabledHintButton
                  hubWritesAllowed={hubWritesAllowed}
                  disabledReason={returnDisabledReason}
                  variant="secondary"
                  size="sm"
                  onClick={onOpenReturnModal}
                  disabled={returnMaterialsState.isPending}
                  isLoading={returnMaterialsState.isPending}
                >
                  <Undo2 className="h-4 w-4" aria-hidden /> Return allocated material
                </DisabledHintButton>
                  </>
                ) : null}
              </div>
              {cancelState.error ? (
                <p className="text-xs text-rose-700">{cancelState.error.message}</p>
              ) : null}
            </div>
          </>
        ) : null}
      </CardBody>
    </Card>
  );
}